import { useState, useContext, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { AppContexts } from "../../../providers";

const AnnouncementModal = () => {
  const { user, setUser } = useContext(AppContexts.AuthContext);
  const { dismissAnnouncement } = useContext(AppContexts.AnnouncementsContext);
  const [currentIndex, setCurrentIndex] = useState(0);
  const modalRef = useRef(null);

  const announcements = user?.announcements || [];
  const total = announcements.length;
  const current = announcements[currentIndex];

  // Lock body scroll when modal is open
  useEffect(() => {
    if (total === 0) return;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, [total]);

  // Stop UIkit from stealing focus
  useEffect(() => {
    if (total === 0) return;
    const el = modalRef.current;
    if (!el) return;
    const stop = (e) => e.stopPropagation();
    el.addEventListener("focusin", stop, true);
    el.addEventListener("focusout", stop, true);
    return () => {
      el.removeEventListener("focusin", stop, true);
      el.removeEventListener("focusout", stop, true);
    };
  }, [total]);

  if (!user || total === 0) return null;

  const handleDismiss = async () => {
    await dismissAnnouncement(user.user_id, current.id);
    const remaining = announcements.filter((a) => a.id !== current.id);
    setUser({ ...user, announcements: remaining });
    // If there are more announcements, stay at current index or go back
    if (currentIndex >= remaining.length && remaining.length > 0) {
      setCurrentIndex(remaining.length - 1);
    }
  };

  const handleNext = () => {
    if (currentIndex < total - 1) setCurrentIndex(currentIndex + 1);
  };

  const handlePrev = () => {
    if (currentIndex > 0) setCurrentIndex(currentIndex - 1);
  };

  return createPortal(
    <div className="rbr-announcement-modal" ref={modalRef}>
      <div className="rbr-announcement-modal-backdrop" />
      <div className="rbr-announcement-modal-card">
        <div className="rbr-announcement-modal-header">
          <span className="uk-icon" uk-icon="bell"></span>
          <span>
            Announcement{total > 1 ? `s (${currentIndex + 1} of ${total})` : ""}
          </span>
        </div>

        <div className="rbr-announcement-modal-body">
          <h3 className="rbr-announcement-modal-title">{current.title}</h3>
          <p className="rbr-announcement-modal-desc">{current.description}</p>
          {current.link && (
            <a
              href={current.link}
              target="_blank"
              rel="noopener noreferrer"
              className="rbr-announcement-modal-link"
            >
              Learn More <span uk-icon="icon: chevron-right; ratio: 0.8"></span>
            </a>
          )}
        </div>

        <div className="rbr-announcement-modal-footer">
          {total > 1 && (
            <div className="rbr-announcement-modal-nav">
              <button
                type="button"
                className="uk-button hms-btn hms-white-btn"
                disabled={currentIndex === 0}
                onClick={handlePrev}
              >
                Previous
              </button>
              <button
                type="button"
                className="uk-button hms-btn hms-white-btn"
                disabled={currentIndex === total - 1}
                onClick={handleNext}
              >
                Next
              </button>
            </div>
          )}
          <button
            type="button"
            className="uk-button hms-btn hms-red-btn"
            onClick={handleDismiss}
          >
            Got it
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default AnnouncementModal;
